import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type AppRole = 'owner' | 'manager' | 'mechanic'

interface AuthUser {
  id: string
  name: string
  phone: string
  role: AppRole
  roles?: AppRole[]
  tenantId: string
  timezone?: string
  /** Enabled feature modules for the tenant. Absent key => enabled (matches API). */
  modules?: Record<string, boolean>
}

interface OwnerSnapshot {
  user: AuthUser
  accessToken: string
  refreshToken: string | null
}

interface AuthState {
  user: AuthUser | null
  accessToken: string | null
  refreshToken: string | null
  /** Saved owner session when impersonating a manager */
  impersonatedBy: OwnerSnapshot | null
  setAuth: (user: AuthUser, accessToken: string, refreshToken?: string) => void
  setAccessToken: (token: string) => void
  setModules: (modules: Record<string, boolean>) => void
  startImpersonation: (managerUser: AuthUser, managerToken: string, ownerSnapshot: OwnerSnapshot) => void
  stopImpersonation: () => void
  logout: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      impersonatedBy: null,
      setAuth: (user, accessToken, refreshToken) =>
        set({ user, accessToken, refreshToken: refreshToken ?? null, impersonatedBy: null }),
      setAccessToken: (accessToken) => set({ accessToken }),
      setModules: (modules) => set((s) => (s.user ? { user: { ...s.user, modules } } : s)),
      startImpersonation: (managerUser, managerToken, ownerSnapshot) =>
        set({ user: managerUser, accessToken: managerToken, refreshToken: null, impersonatedBy: ownerSnapshot }),
      stopImpersonation: () =>
        set((s) => s.impersonatedBy
          ? { user: s.impersonatedBy.user, accessToken: s.impersonatedBy.accessToken, refreshToken: s.impersonatedBy.refreshToken, impersonatedBy: null }
          : s,
        ),
      logout: () => set({ user: null, accessToken: null, refreshToken: null, impersonatedBy: null }),
    }),
    {
      name: 'velorent-auth',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        impersonatedBy: state.impersonatedBy,
      }),
    },
  ),
)
